import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { useSystemNotifications, SystemNotification } from '@/hooks/useSystemNotifications';
import Link from 'next/link';

const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'agora mesmo';
    if (diffInSeconds < 3600) return `há ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `há ${Math.floor(diffInSeconds / 3600)} h`;
    return `há ${Math.floor(diffInSeconds / 86400)} dias`;
};

/**
 * Componente React `NotificationPopover`.
 * @returns {Element} Retorna um valor do tipo `Element`.
 */
export const NotificationPopover = ({ compact, openUp }: { compact?: boolean; openUp?: boolean } = {}) => {
    const { notifications, count, hasHighSeverity, markAsRead, markAllAsRead } = useSystemNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

    // Posiciona o painel `fixed` ancorado no sino. O painel é portalizado pro body
    // (ver render) para escapar do contexto de empilhamento da sidebar (z-20) — sem
    // isso ele fica preso atrás do modal do lead (z-[9999]), por mais alto que seja o z.
    const computePosition = React.useCallback(() => {
        const rect = popoverRef.current?.getBoundingClientRect();
        if (!rect || typeof window === 'undefined') return;
        const Z = 10010; // acima do modal do lead (9999) e seus filhos (10001)
        if (window.innerWidth < 640) {
            setPanelStyle({ position: 'fixed', top: 64, left: 8, right: 8, zIndex: Z });
        } else if (openUp) {
            setPanelStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 8, left: rect.left, width: 384, zIndex: Z });
        } else {
            setPanelStyle({ position: 'fixed', top: rect.bottom + 8, left: Math.max(8, rect.right - 384), width: 384, zIndex: Z });
        }
    }, [openUp]);

    // Fecha ao clicar fora — considera o botão E o painel portalizado (que fica fora do popoverRef)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const t = event.target as Node;
            if (popoverRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Recalcula a posição ao abrir e em resize/scroll enquanto aberto
    useEffect(() => {
        if (!isOpen) return;
        computePosition();
        const onChange = () => computePosition();
        window.addEventListener('resize', onChange);
        window.addEventListener('scroll', onChange, true);
        return () => {
            window.removeEventListener('resize', onChange);
            window.removeEventListener('scroll', onChange, true);
        };
    }, [isOpen, computePosition]);

    const handleNotificationClick = (id: string) => {
        markAsRead(id);
        setIsOpen(false);
    };

    const getIcon = (type: SystemNotification['type']) => {
        switch (type) {
            case 'SYSTEM_ALERT':
                return <AlertTriangle className="w-5 h-5 text-red-500" />;
            case 'SYSTEM_WARNING':
                return <AlertTriangle className="w-5 h-5 text-orange-500" />;
            case 'SYSTEM_SUCCESS':
                return <CheckCircle2 className="w-5 h-5 text-green-500" />;
            case 'SYSTEM_INFO':
            default:
                return <Bell className="w-5 h-5 text-primary-500" />;
        }
    };

    const getBgColor = (type: SystemNotification['type']) => {
        switch (type) {
            case 'SYSTEM_ALERT':
                return 'bg-red-50 dark:bg-red-900/20';
            case 'SYSTEM_WARNING':
                return 'bg-orange-50 dark:bg-orange-900/20';
            case 'SYSTEM_SUCCESS':
                return 'bg-green-50 dark:bg-green-900/20';
            case 'SYSTEM_INFO':
            default:
                return 'bg-primary-50 dark:bg-primary-900/20';
        }
    };

    return (
        <div className="relative" ref={popoverRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-label={`Notificações: ${count} novas`}
                className={compact
                  ? "w-8 h-8 flex items-center justify-center rounded text-gray-400 dark:text-[#666] hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#242424] relative transition-colors"
                  : "p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 rounded-full relative transition-colors focus-visible-ring"}
            >
                <Bell size={compact ? 16 : 20} strokeWidth={compact ? 1.5 : 2} aria-hidden="true" />
                {count > 0 && (
                    <span
                        className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ring-2 ring-white dark:ring-dark-card ${hasHighSeverity ? 'bg-red-500' : 'bg-primary-500'
                            }`}
                        aria-hidden="true"
                    />
                )}
            </button>

            {isOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={panelRef}
                    style={panelStyle}
                    className={`bg-white dark:bg-slate-900 rounded shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in duration-200 ${openUp ? 'slide-in-from-bottom-2' : 'slide-in-from-top-2'}`}
                    role="dialog"
                    aria-label="Central de Notificações"
                >
                    <div className="p-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-slate-50/50 dark:bg-white/5">
                        <h3 className="font-semibold text-slate-900 dark:text-white">Notificações</h3>
                        <div className="flex items-center gap-2">
                            {count > 0 && (
                                <button
                                    onClick={() => markAllAsRead()}
                                    className="text-xs text-slate-500 hover:text-primary-600 transition-colors"
                                >
                                    Marcar todas como lidas
                                </button>
                            )}
                            {count > 0 && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                                    {count} nova{count !== 1 && 's'}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center flex flex-col items-center text-slate-500 dark:text-slate-400">
                                <div className="w-12 h-12 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mb-3">
                                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                                </div>
                                <p className="font-medium text-slate-900 dark:text-white">Tudo limpo!</p>
                                <p className="text-sm">Você não tem notificações.</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-slate-100 dark:divide-white/5">
                                {notifications.map((notification) => (
                                    <li key={notification.id} className={notification.readAt ? 'opacity-60 bg-slate-50/30 dark:bg-white/5' : ''}>
                                        <Link
                                            href={notification.actionLink || '#'}
                                            onClick={() => handleNotificationClick(notification.id)}
                                            className="flex gap-4 p-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group"
                                        >
                                            <div className={`mt-1 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${getBgColor(notification.type)}`}>
                                                {getIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start">
                                                    <p className={`text-sm font-medium group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors ${notification.readAt ? 'text-slate-600 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>
                                                        {notification.title}
                                                    </p>
                                                    {!notification.readAt && (
                                                        <span className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 flex items-center gap-1">
                                                    <Clock size={12} />
                                                    {getTimeAgo(notification.timestamp)}
                                                </p>
                                            </div>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="p-3 border-t border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/5 text-center">
                        <Link
                            href="/dashboard"
                            onClick={() => setIsOpen(false)}
                            className="text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                            Ver Dashboard Completo
                        </Link>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
